import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'

interface Prompt {
  id: string
  title: string
  content: string
  category: string
  createdAt: string
  updatedAt?: string
}

export default function PromptLibraryPage() {
  const [prompts, setPrompts] = useState<Prompt[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editTitle, setEditTitle] = useState('')
  const [editContent, setEditContent] = useState('')
  const [editCategory, setEditCategory] = useState('')
  const [showAdd, setShowAdd] = useState(false)
  const [saving, setSaving] = useState(false)

  const fetchPrompts = useCallback(async () => {
    try {
      const { data, error: err } = await supabase
        .from('prompt_library')
        .select('*')
        .eq('is_active', true)
        .order('display_order')
      if (err) throw new Error(err.message)
      setPrompts((data || []).map((r: any) => ({
        id: r.id,
        title: r.title,
        content: r.content,
        category: r.category || 'general',
        createdAt: r.created_at,
        updatedAt: r.updated_at,
      })))
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load prompts')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchPrompts() }, [fetchPrompts])

  const handleAdd = async () => {
    if (!editTitle.trim() || !editContent.trim()) return
    setSaving(true)
    try {
      const { error: err } = await supabase
        .from('prompt_library')
        .insert({
          title: editTitle,
          content: editContent,
          category: editCategory || 'general',
          user_id: '00000000-0000-0000-0000-000000000000',
          is_active: true,
        })
        .select()
        .single()
      if (err) throw new Error(err.message)
      setShowAdd(false)
      setEditTitle('')
      setEditContent('')
      setEditCategory('')
      fetchPrompts()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save')
    } finally {
      setSaving(false)
    }
  }

  const handleUpdate = async (id: string) => {
    setSaving(true)
    try {
      const { error: err } = await supabase
        .from('prompt_library')
        .update({ title: editTitle, content: editContent, category: editCategory })
        .eq('id', id)
      if (err) throw new Error(err.message)
      setEditingId(null)
      fetchPrompts()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id: string, title: string) => {
    if (!confirm(`Delete prompt "${title}"?`)) return
    try {
      await supabase
        .from('prompt_library')
        .update({ is_active: false })
        .eq('id', id)
      fetchPrompts()
    } catch { /* ignore */ }
  }

  const startEdit = (prompt: Prompt) => {
    setEditingId(prompt.id)
    setEditTitle(prompt.title)
    setEditContent(prompt.content)
    setEditCategory(prompt.category)
  }

  if (loading) return <div className="flex items-center justify-center h-64"><div className="text-gray-400">Loading prompts...</div></div>

  return (
    <div className="p-6 mx-auto" style={{ maxWidth: '100%' }}>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">📋 Prompt Library</h1>
        <button
          onClick={() => { setShowAdd(true); setEditTitle(''); setEditContent(''); setEditCategory('general') }}
          className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-500 transition-colors"
        >
          + Add Prompt
        </button>
      </div>

      {error && <div className="mb-4 bg-red-900/30 border border-red-700 rounded-lg p-3 text-red-300 text-sm">{error}</div>}

      {/* Add/Edit Form */}
      {(showAdd || editingId) && (
        <div className="mb-6 bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">{editingId ? 'Edit Prompt' : 'New Prompt'}</h3>
          <input
            value={editTitle}
            onChange={e => setEditTitle(e.target.value)}
            placeholder="Prompt title (e.g. Weekly Portfolio Review)"
            className="w-full px-3 py-2 mb-3 rounded border border-gray-300 bg-white text-sm text-gray-900 focus:outline-none focus:border-blue-500"
          />
          <div className="flex gap-3 mb-3">
            <input
              value={editCategory}
              onChange={e => setEditCategory(e.target.value)}
              placeholder="Category (e.g. weekly, monthly, ad-hoc)"
              className="flex-1 px-3 py-2 rounded border border-gray-300 bg-white text-sm text-gray-900 focus:outline-none focus:border-blue-500"
            />
          </div>
          <textarea
            value={editContent}
            onChange={e => setEditContent(e.target.value)}
            placeholder="Prompt content (markdown)..."
            className="w-full px-3 py-2 rounded border border-gray-300 bg-white text-sm text-gray-900 font-mono resize-y focus:outline-none focus:border-blue-500"
            rows={10}
          />
          <div className="flex gap-2 mt-3">
            <button
              onClick={() => editingId ? handleUpdate(editingId) : handleAdd()}
              disabled={saving || !editTitle.trim() || !editContent.trim()}
              className="px-4 py-2 rounded text-sm bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-50"
            >
              {saving ? 'Saving...' : editingId ? 'Update' : 'Create'}
            </button>
            <button
              onClick={() => { setShowAdd(false); setEditingId(null) }}
              className="px-4 py-2 rounded text-sm bg-gray-700 text-gray-300 hover:bg-gray-600"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Prompt Cards */}
      {prompts.length === 0 ? (
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-8 text-center text-gray-400">
          <p className="mb-2">No prompts yet.</p>
          <p className="text-sm">Click "+ Add Prompt" to create reusable prompts for your AI chat.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {prompts.map(prompt => (
            <div key={prompt.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:border-gray-300 shadow-sm transition-colors">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-sm font-semibold text-gray-900">{prompt.title}</h3>
                    <span className="px-2 py-0.5 text-[10px] rounded bg-gray-100 text-gray-500">{prompt.category}</span>
                  </div>
                  <p className="text-xs text-gray-500 line-clamp-2">{prompt.content.slice(0, 150)}...</p>
                </div>
                <div className="flex items-center gap-1 ml-3">
                  <button onClick={() => startEdit(prompt)} className="px-2 py-1 text-xs rounded bg-gray-700 text-gray-300 hover:bg-gray-600">✏️</button>
                  <button onClick={() => handleDelete(prompt.id, prompt.title)} className="px-2 py-1 text-xs rounded bg-gray-700 text-gray-300 hover:bg-red-700 hover:text-white">🗑️</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
